/**
 * PersonalInfoPage -- Edit base personal info and per-site overrides.
 *
 * Left column: base personal info fields.
 * Right column: site selector + field-by-field overrides with OverrideField.
 */
import { useEffect, useState, useRef, useCallback, type FormEvent } from 'react';
import { useContentStore, useContentActions } from '../../state/cms/useContentStore';
import { useSiteStore, useSiteActions } from '../../state/cms/useSiteStore';
import { Panel } from '../../components/ui/Panel';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { LoadingState } from '../../components/ui/LoadingState';
import { OverrideField } from '../../components/cms/OverrideField';
import { AiAssistButton } from '../../components/cms/AiAssistButton';
import { AiSuggestionPanel } from '../../components/cms/AiSuggestionPanel';
import { useSaveHandler } from '../../hooks/useSaveHandler';
import { useAiAssist } from '../../hooks/useAiAssist';
import { aiApi } from '../../services/ai.api';
import { logger } from '../../services/logger.client';
import type { PersonalInfoBase } from '../../types/cms.types';
import type { GenerateContentData } from '../../types/ai.types';

const log = logger.child({ module: 'PersonalInfoPage' });

const PERSONAL_INFO_FIELDS: { key: keyof PersonalInfoBase; label: string }[] = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'tagline', label: 'Tagline' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'location', label: 'Location' },
  { key: 'linkedin_url', label: 'LinkedIn URL' },
  { key: 'github_url', label: 'GitHub URL' },
  { key: 'website_url', label: 'Website URL' },
  { key: 'summary', label: 'Summary' },
];

export function PersonalInfoPage() {
  const personalInfo = useContentStore((s) => s.personalInfo);
  const overrides = useContentStore((s) => s.personalInfoOverrides);
  const isLoading = useContentStore((s) => s.isLoading);
  const error = useContentStore((s) => s.error);
  const {
    fetchPersonalInfo,
    updatePersonalInfo,
    fetchOverrides,
    upsertOverride,
    deleteOverride,
  } = useContentActions();

  const sites = useSiteStore((s) => s.sites);
  const { fetchSites } = useSiteActions();

  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [localInfo, setLocalInfo] = useState<Partial<PersonalInfoBase>>({});
  const { isSaving, saveMessage, saveError, execute: executeSave } = useSaveHandler({
    getStoreError: () => useContentStore.getState().error,
  });
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const overrideTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /* ---- AI Assist: Summary generation ---- */
  const summaryAi = useAiAssist<
    { type: 'summary'; context: { current_title?: string; key_skills?: string[]; name?: string } },
    GenerateContentData
  >({
    apiCall: (input) => aiApi.generateContent(input),
    onAccept: (data) => {
      setLocalInfo((prev) => ({ ...prev, summary: data.content }));
    },
  });

  useEffect(() => {
    log.info('PersonalInfoPage mounted');
    void fetchPersonalInfo();
    void fetchSites();
  }, [fetchPersonalInfo, fetchSites]);

  useEffect(() => {
    if (personalInfo) {
      setLocalInfo(personalInfo);
    }
  }, [personalInfo]);

  useEffect(() => {
    if (selectedSiteId) {
      void fetchOverrides(selectedSiteId);
    }
  }, [selectedSiteId, fetchOverrides]);

  function handleFieldChange(key: keyof PersonalInfoBase, value: string) {
    setLocalInfo((prev) => ({ ...prev, [key]: value || null }));
  }

  async function handleSaveBase(e: FormEvent) {
    e.preventDefault();
    log.info('Saving personal info base');
    await executeSave(() => updatePersonalInfo(localInfo));
  }

  const handleOverrideSave = useCallback(async (fieldName: string, value: string) => {
    if (!selectedSiteId) return;
    try {
      await upsertOverride(selectedSiteId, fieldName, value);
      const storeErr = useContentStore.getState().error;
      if (storeErr) {
        setOverrideError(storeErr);
        if (overrideTimerRef.current) clearTimeout(overrideTimerRef.current);
        overrideTimerRef.current = setTimeout(() => setOverrideError(null), 5000);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save override';
      setOverrideError(msg);
      if (overrideTimerRef.current) clearTimeout(overrideTimerRef.current);
      overrideTimerRef.current = setTimeout(() => setOverrideError(null), 5000);
    }
  }, [selectedSiteId, upsertOverride]);

  const handleOverrideClear = useCallback(async (fieldName: string) => {
    if (!selectedSiteId) return;
    try {
      await deleteOverride(selectedSiteId, fieldName);
      const storeErr = useContentStore.getState().error;
      if (storeErr) {
        setOverrideError(storeErr);
        if (overrideTimerRef.current) clearTimeout(overrideTimerRef.current);
        overrideTimerRef.current = setTimeout(() => setOverrideError(null), 5000);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to clear override';
      setOverrideError(msg);
      if (overrideTimerRef.current) clearTimeout(overrideTimerRef.current);
      overrideTimerRef.current = setTimeout(() => setOverrideError(null), 5000);
    }
  }, [selectedSiteId, deleteOverride]);

  function getOverrideValue(fieldName: string): string | null {
    const override = overrides.find((o) => o.field_name === fieldName);
    return override?.field_value ?? null;
  }

  return (
    <div className="cms-page">
      <header className="cms-page__header">
        <h1 className="cms-page__title">Personal Info</h1>
      </header>

      <LoadingState loading={isLoading && !personalInfo} error={error}>
        <div className="cms-two-column">
          {/* ---------- Left: Base Info ---------- */}
          <div className="cms-two-column__left">
            <Panel>
              <Panel.Header>Base Information</Panel.Header>
              <Panel.Content>
                <form
                  className="editor-field-group"
                  onSubmit={handleSaveBase}
                  noValidate
                >
                  {PERSONAL_INFO_FIELDS.filter((f) => f.key !== 'id' && f.key !== 'summary').map(
                    (field) => (
                      <Input
                        key={field.key}
                        label={field.label}
                        value={(localInfo[field.key] as string) ?? ''}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        placeholder={field.label}
                      />
                    ),
                  )}
                  <div className="input-wrapper">
                    <div className="ai-label-row">
                      <label className="input-label" htmlFor="pi-summary">
                        Summary
                      </label>
                      <AiAssistButton
                        onClick={() =>
                          void summaryAi.trigger({
                            type: 'summary',
                            context: {
                              name: [localInfo.first_name, localInfo.last_name]
                                .filter(Boolean)
                                .join(' ') || undefined,
                              current_title: (localInfo.tagline as string) || undefined,
                            },
                          })
                        }
                        isLoading={summaryAi.isLoading}
                        label="Generate summary with AI"
                      />
                    </div>
                    <textarea
                      id="pi-summary"
                      className="input input--textarea"
                      rows={4}
                      value={(localInfo.summary as string) ?? ''}
                      onChange={(e) => handleFieldChange('summary', e.target.value)}
                      placeholder="Professional summary..."
                    />
                    <AiSuggestionPanel
                      suggestion={summaryAi.suggestion ? summaryAi.suggestion.content : null}
                      error={summaryAi.error}
                      isLoading={summaryAi.isLoading}
                      onAccept={summaryAi.accept}
                      onDismiss={summaryAi.dismiss}
                    />
                  </div>
                  <div className="cms-page__actions-row">
                    {saveError && (
                      <span className="editor-page__save-msg editor-page__save-msg--error">
                        {saveError}
                      </span>
                    )}
                    {saveMessage && (
                      <span className="editor-page__save-msg">{saveMessage}</span>
                    )}
                    <Button
                      variant="primary"
                      size="md"
                      loading={isSaving}
                      type="submit"
                    >
                      Save
                    </Button>
                  </div>
                </form>
              </Panel.Content>
            </Panel>
          </div>

          {/* ---------- Right: Per-Site Overrides ---------- */}
          <div className="cms-two-column__right">
            <Panel>
              <Panel.Header>Per-Site Overrides</Panel.Header>
              <Panel.Content>
                <div className="input-wrapper">
                  <label className="input-label" htmlFor="override-site-select">
                    Select Site
                  </label>
                  <select
                    id="override-site-select"
                    className="input"
                    value={selectedSiteId}
                    onChange={(e) => setSelectedSiteId(e.target.value)}
                  >
                    <option value="">-- Choose a site --</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </select>
                </div>

                {overrideError && (
                  <span className="editor-page__save-msg editor-page__save-msg--error">
                    {overrideError}
                  </span>
                )}

                {selectedSiteId && personalInfo && (
                  <div className="cms-override-list">
                    {PERSONAL_INFO_FIELDS.filter((f) => f.key !== 'id').map(
                      (field) => (
                        <OverrideField
                          key={field.key}
                          fieldName={field.key}
                          label={field.label}
                          baseValue={personalInfo[field.key] as string | null}
                          overrideValue={getOverrideValue(field.key)}
                          onSave={handleOverrideSave}
                          onClear={handleOverrideClear}
                        />
                      ),
                    )}
                  </div>
                )}

                {!selectedSiteId && (
                  <p className="list-empty">
                    Select a site to manage its overrides.
                  </p>
                )}
              </Panel.Content>
            </Panel>
          </div>
        </div>
      </LoadingState>
    </div>
  );
}
